import { cleanUrl } from '../shared/clean'
import type { ResolveResult } from '../shared/types'

/**
 * 打 API 解析，失敗時降級成本地清理。
 *
 * 降級的判準是「本地還做得到嗎」，不是「錯在哪一層」：
 * 跟隨轉址一定要 server（瀏覽器有 CORS），但**拔 query 前端自己就會做**。
 * 所以只要輸入是個網址，離線、限流（429）、超出免費額度（1027）、
 * 上游 5xx —— 全部都該給使用者一個乾淨的純網址加一句說明，
 * 而不是一則錯誤訊息。
 *
 * 唯一該硬失敗的是 400：那代表「這根本不是能處理的網址」，
 * 本地清理同樣做不到，沒有降級空間。
 */
export async function resolve(input: string, signal?: AbortSignal): Promise<ResolveResult> {
  const local = cleanUrl(input)

  const degrade = (reason: string): ResolveResult => {
    if (!local) throw new Error('看不出這是一個網址')
    return {
      input,
      final: local.full,
      clean: local.clean,
      chain: [{ url: local.full, status: 0 }],
      notes: local.notes,
      resolved: false,
      warning: reason,
    }
  }

  let res: Response
  try {
    res = await fetch(`/api/resolve?url=${encodeURIComponent(input)}`, { signal })
  } catch (err) {
    if (signal?.aborted) throw err
    return degrade('連不上伺服器，只做了本地清理（沒有跟隨轉址）')
  }

  if (res.ok) return (await res.json()) as ResolveResult

  const body = (await res.json().catch(() => null)) as { error?: string } | null

  if (res.status === 400) {
    throw new Error(body?.error ?? '無法解析這個網址')
  }

  return degrade(
    res.status === 429
      ? '伺服器忙碌中，只做了本地清理（沒有跟隨轉址）'
      : `伺服器暫時無法解析（${res.status}），只做了本地清理`,
  )
}

export type CopyOutcome = 'copied' | 'needs-gesture' | 'failed'

/**
 * 寫剪貼簿。從 share target 進來時可能沒有 user activation，
 * Clipboard API 會直接拒絕 —— 這種情況要回報給 UI 換成手動按鈕。
 */
export async function copy(value: string): Promise<CopyOutcome> {
  if (!navigator.clipboard?.writeText) return 'needs-gesture'
  try {
    await navigator.clipboard.writeText(value)
    return 'copied'
  } catch (err) {
    return err instanceof DOMException && err.name === 'NotAllowedError'
      ? 'needs-gesture'
      : 'failed'
  }
}

export function buzz(ms = 24): void {
  navigator.vibrate?.(ms)
}

/**
 * 嘗試關掉 PWA 視窗回到剛剛的 app。
 *
 * 這件事 web 平台沒有保證：window.close() 原本只給 script 開的視窗用，
 * 在已安裝的 standalone PWA 上 Chromium 通常會放行，但不是規格保證，
 * 也沒有任何 API 能「回到上一個 app」。所以回傳有沒有成功，讓 UI 有 fallback。
 */
export function tryCloseWindow(): Promise<boolean> {
  return new Promise((done) => {
    try {
      window.close()
    } catch {
      /* 忽略：接下來的檢查會處理 */
    }
    // 關掉的話這個 timer 根本不會跑到。
    setTimeout(() => done(window.closed), 250)
  })
}

/* ---------- 最近紀錄（localStorage，10 筆） ---------- */

const HISTORY_KEY = 'deshorten:history:v1'
const HISTORY_MAX = 10

export interface HistoryEntry {
  input: string
  clean: string
  at: number
}

export function readHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as HistoryEntry[]).slice(0, HISTORY_MAX) : []
  } catch {
    return []
  }
}

export function pushHistory(entry: Omit<HistoryEntry, 'at'>): HistoryEntry[] {
  const next = [
    { ...entry, at: Date.now() },
    ...readHistory().filter((e) => e.clean !== entry.clean),
  ].slice(0, HISTORY_MAX)
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next))
  } catch {
    /* 無痕模式下 localStorage 會丟 —— 紀錄不是核心功能，靜默失敗 */
  }
  return next
}

export function clearHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY)
  } catch {
    /* 同上 */
  }
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
